import { useTheme } from '@/contexts/ThemeContext';
import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle, ScrollText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function DisclaimerModal({ onAccepted }) {
  const { isDarkMode } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API}/api/disclaimer`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setText(d.text || ''))
      .catch(() => {});
  }, []);

  const handleScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 10) {
      setScrolled(true);
    }
  };

  const handleAccept = async () => {
    if (!checked || loading) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/api/disclaimer/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      onAccepted();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
    }}>
      <div style={{
        background: isDarkMode ? '#1c1c1e' : '#ffffff', borderRadius: '20px',
        width: '100%', maxWidth: '560px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        display: 'flex', flexDirection: 'column', maxHeight: '90vh'
      }}>
        {/* Header */}
        <div style={{ padding: '24px 24px 16px', borderBottom: '0.5px solid var(--apple-border, #e5e5ea)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <ScrollText size={22} color="#2563eb" />
            <h2 style={{ fontSize: '18px', fontWeight: 700, color: isDarkMode ? '#f5f5f7' : '#1d1d1f', margin: 0 }}>
              Terms of Use & Disclaimer
            </h2>
          </div>
          <p style={{ fontSize: '13px', color: isDarkMode ? '#aeaeb2' : '#6e6e73', margin: 0 }}>
            Please read and scroll through the full terms before accepting.
          </p>
        </div>

        {/* Scrollable text */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          style={{
            flex: 1, overflowY: 'auto', padding: '20px 24px',
            minHeight: '200px', maxHeight: '360px'
          }}
        >
          {text ? text.split('\n').map((line, i) => {
            const trimmed = line.trim();
            if (!trimmed) return <div key={i} style={{ height: '10px' }} />;
            // Main title
            if (trimmed === 'TERMS OF USE & SERVICE DISCLAIMER') return (
              <p key={i} style={{ fontSize: '16px', fontWeight: 800, color: isDarkMode ? '#ffffff' : '#1d1d1f', margin: '0 0 16px', lineHeight: 1.4, letterSpacing: '-0.01em' }}>{trimmed}</p>
            );
            // Intro line
            if (trimmed.startsWith('By using t09n.com')) return (
              <p key={i} style={{ fontSize: '13px', color: isDarkMode ? '#aeaeb2' : '#6e6e73', margin: '0 0 20px', lineHeight: 1.7, fontStyle: 'italic' }}>{trimmed}</p>
            );
            // Numbered section headings
            if (/^\d+\.\s+[A-Z]/.test(trimmed)) return (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', margin: '16px 0 6px', padding: '10px 12px', background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(37,99,235,0.05)', borderRadius: '8px', borderLeft: '3px solid #2563eb' }}>
                <p style={{ fontSize: '12px', fontWeight: 700, color: '#2563eb', margin: 0, lineHeight: 1.5, whiteSpace: 'nowrap' }}>{trimmed.match(/^\d+/)[0]}.</p>
                <p style={{ fontSize: '12px', fontWeight: 700, color: isDarkMode ? '#f5f5f7' : '#1d1d1f', margin: 0, lineHeight: 1.5 }}>{trimmed.replace(/^\d+\.\s*/, '')}</p>
              </div>
            );
            // Final acceptance line
            if (trimmed.startsWith('By checking')) return (
              <p key={i} style={{ fontSize: '13px', fontWeight: 600, color: isDarkMode ? '#f5f5f7' : '#1d1d1f', margin: '20px 0 4px', lineHeight: 1.6, padding: '12px', background: isDarkMode ? 'rgba(37,99,235,0.1)' : 'rgba(37,99,235,0.06)', borderRadius: '8px' }}>{trimmed}</p>
            );
            return (
              <p key={i} style={{ fontSize: '13px', color: isDarkMode ? '#c7c7cc' : '#4a4a4a', margin: '0 0 4px', lineHeight: 1.75, paddingLeft: '4px' }}>{trimmed}</p>
            );
          }) : <p style={{ color: '#aeaeb2' }}>Loading...</p>}
        </div>

        {/* Scroll indicator */}
        {!scrolled && (
          <div style={{
            padding: '8px 24px', background: 'rgba(37,99,235,0.06)',
            borderTop: '0.5px solid rgba(37,99,235,0.15)',
            fontSize: '12px', color: '#2563eb', textAlign: 'center', fontWeight: 500
          }}>
            ↓ Scroll to the bottom to enable acceptance
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: isDarkMode ? '0.5px solid #3a3a3c' : '0.5px solid #e5e5ea' }}>
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: '10px',
            cursor: scrolled ? 'pointer' : 'not-allowed', marginBottom: '14px',
            opacity: scrolled ? 1 : 0.4
          }}>
            <input
              type="checkbox"
              checked={checked}
              disabled={!scrolled}
              onChange={e => setChecked(e.target.checked)}
              style={{ marginTop: '2px', width: '16px', height: '16px', accentColor: '#2563eb', cursor: scrolled ? 'pointer' : 'not-allowed' }}
            />
            <span style={{ fontSize: '13px', color: isDarkMode ? '#f5f5f7' : '#1d1d1f', lineHeight: 1.5 }}>
              I have read and agree to the Terms of Use & Service Disclaimer.
            </span>
          </label>

          <button
            onClick={handleAccept}
            disabled={!checked || loading}
            style={{
              width: '100%', padding: '12px',
              background: checked ? '#2563eb' : isDarkMode ? '#3a3a3c' : '#e5e5ea',
              color: checked ? '#fff' : '#aeaeb2',
              border: 'none', borderRadius: '12px',
              fontSize: '15px', fontWeight: 600,
              cursor: checked ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
            }}
          >
            {loading ? 'Saving...' : <><CheckCircle size={16} /> I Accept & Continue</>}
          </button>
        </div>
      </div>
    </div>
  );
}
